import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { PayPalCheckout } from "@/components/PayPalCheckout";
import { InstalmentCheckout, InstalmentCue } from "@/components/Instalments";
import { toast } from "sonner";
import { api, errMsg, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { PromoBox, Totals } from "@/components/CartDrawer";
import { RewardsBox } from "@/components/RewardsBox";
import { BankTransferOption } from "@/components/BankTransferOption";
import { PageHeader, Field, inputCls, FinanceCue } from "@/components/common";
import { useMeta } from "@/hooks/useMeta";
import { DeliveryOptions } from "@/components/DeliveryOptions";

export default function Checkout() {
  const { items, quote, promo, rewards, payload, ship } = useCart();
  const { user } = useAuth();
  const [f, setF] = useState<any>({ email: "", name: "", phone: "", financing: false });
  const [busy, setBusy] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  useMeta("Checkout | Somnio.Co");

  useEffect(() => { if (user) setF((x: any) => ({ ...x, email: x.email || user.email, name: x.name || user.name, phone: x.phone || user.phone || "" })); }, [user]);

  const pay = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/checkout", { ...f, ...ship, items: payload(), promo_code: promo || null, rewards: user ? rewards : null, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  if (!items.length) return (
    <div className="max-w-xl mx-auto text-center py-32 px-5" data-testid="checkout-empty">
      <p className="font-display text-3xl mb-8">Your bag is empty</p>
      <Link to="/collections/all" className="btn-ink px-9 py-4 text-xs tracking-[0.25em] uppercase">Continue Shopping</Link>
    </div>
  );

  return (
    <div data-testid="checkout-page">
      <PageHeader eyebrow="Secure Checkout" title="Checkout" />
      <div className="max-w-[1200px] mx-auto px-5 md:px-12 grid lg:grid-cols-[1fr_420px] gap-12">
        <div className="space-y-4">
        <form ref={formRef} onSubmit={pay} className="space-y-6" data-testid="checkout-form">
          {!user && <p className="text-sm bg-[var(--c-muted)] p-4">Have an account? <Link to="/login?next=/checkout" className="underline">Sign in</Link> to track this order and save your details.</p>}
          <Field label="Email"><input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} data-testid="checkout-email-input" /></Field>
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Full name"><input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="checkout-name-input" /></Field>
            <Field label="Phone"><input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inputCls} data-testid="checkout-phone-input" /></Field>
          </div>
          <label className="flex items-start gap-3 text-sm border border-neutral-200 bg-white p-4 cursor-pointer" data-testid="checkout-financing-checkbox">
            <input type="checkbox" checked={f.financing} onChange={(e) => setF({ ...f, financing: e.target.checked })} className="mt-1 accent-neutral-900" />
            <span>I'd like a specialist to contact me about paying over time. {quote?.total > 0 && <span className="text-neutral-500">Estimated from {money(Math.ceil(quote.total / 36))}/mo.</span>}</span>
          </label>
          <DeliveryOptions />
          <p className="text-xs text-neutral-500">Shipping address and payment details are collected securely on the next step.</p>
          <button disabled={busy || !quote || quote.ship_ok === false} className="btn-ink w-full py-4 text-xs tracking-[0.25em] uppercase flex items-center justify-center gap-3 disabled:opacity-60" data-testid="checkout-pay-stripe-button">
            <Lock size={14} /> {busy ? "Redirecting…" : quote && quote.total <= 0 ? "Place order with rewards" : `Pay ${quote ? money(quote.total) : ""} by card`}
          </button>
        </form>
        {quote && quote.ship_ok !== false && <PayPalCheckout form={f} formRef={formRef} />}
        {quote?.ship_ok !== false && <InstalmentCheckout form={f} formRef={formRef} />}
        {quote?.ship_ok !== false && <BankTransferOption form={f} formRef={formRef} />}
        </div>
        <aside className="border border-neutral-200 bg-white p-7 space-y-5 self-start" data-testid="checkout-summary">
          <p className="eyebrow">Order Summary</p>
          {quote?.lines?.map((l: any, i: number) => (
            <div key={i} className="flex gap-4 text-sm" data-testid="checkout-line">
              <img src={l.image} alt="" className="h-16 w-16 object-contain bg-[var(--c-muted)]" />
              <div className="flex-1"><p className="font-display text-base leading-tight">{l.name}</p><p className="text-xs text-neutral-500">{[l.metal, l.ring_size && `Size ${l.ring_size}`, l.builder && `${l.builder.carat}ct ${l.builder.shape}`].filter(Boolean).join(" · ")} × {l.qty}</p></div>
              <span>{money(l.line_total)}</span>
            </div>
          ))}
          <PromoBox />
          <RewardsBox />
          <Totals quote={quote} />
          {quote?.total > 0 && <><InstalmentCue price={quote.total} testId="checkout-instalment-cue" /><FinanceCue price={quote.total} testId="checkout-finance-cue" /></>}
        </aside>
      </div>
    </div>
  );
}
